import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearCrossSell, clearCustomer, clearCustomerDataOld, i18n, openCallMeModal, RootState, setCustomer, setExamDraft, setGlobalLoading, setProgressBarPercentage, setSoId, setSoIdWeb, SourceEnum, useAppDispatch, useAppSelector, useCreateSoWithCaptcha } from 'src/common';
import { createSoAdditional } from 'src/common/api/apollo/additional/mutations';
import { CreateSoInput, CrossSellInput } from 'src/common/api/apollo/additional/types/types';
import { ArrowRightIcon, BackLink, Button, CaptchaChallenge, Card, CustomerFormAdditional, Flex, FormRef, TermsCheckbox, Text } from 'src/components';
import { colors } from 'src/theme';
import { AppRoutesAdditional, AppRoutesAdditionalCrossell } from '../../_router/app.routes';

const CustomerDataPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isCrossell = pathname.includes('dodatno-zdravstveno-osiguranje-crossell');
  const routes = isCrossell ? AppRoutesAdditionalCrossell : AppRoutesAdditional;
  const customerData = useAppSelector((state: RootState) => state.model.customer.data);
  const examData = useAppSelector((state: RootState) => state.model.exam.data);
  const crossSellData = useAppSelector((state: RootState) => state.model.crossSell.data);
  const globalLoading = useAppSelector((state: RootState) => state.ui?.global ?? false);
  // createSo behind health-api's captcha ladder (see useCreateSoWithCaptcha).
  const { submit: createSo, captcha } = useCreateSoWithCaptcha(createSoAdditional, { page: 'additional-customer' });
  const formRef = useRef<FormRef>(null);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const flow = useAppSelector((state: RootState) => state.state.flow);

  useEffect(() => {
    dispatch(setProgressBarPercentage(30));
    // TODO: Remove this for webshop
    // dispatch(setFlowType('agent'));
  }, []);

  // Sync URL params to Redux so Back link can navigate step-3 → step-2
  useEffect(() => {
    if (customerData?.birthDate) {
      dispatch(setCustomer({ birthDate: customerData.birthDate }));
    }
  }, [customerData?.birthDate, dispatch]);

  const handleFormSuccess = (formData: any) => {
    // Store all customer data in Redux (customer slice)
    dispatch(setCustomer({
      birthDate: customerData?.birthDate,
      firstName: formData?.firstName,
      lastName: formData?.lastName,
      email: formData?.email,
      phoneNumber: formData?.phoneNumber,
    }));
    if (customerData?.birthDate && !!examData) {
      const input: CreateSoInput = {
        email: formData?.email,
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        dateOfBirth: customerData?.birthDate,
        phoneNumber: formData?.phoneNumber,
        productTypeCode: 'dodatno',
        partnerSource: customerData?.partnerSource,
        additionalInsuranceInfo: {
          diagnostic: examData?.diagnostic,
          laboratoryExaminations: examData?.laboratoryExaminations,
          mrCt: examData?.mrCt,
          perMonthPayment: examData?.perMonthPayment,
          physicalExamination: examData?.physicalExamination,
          physicalTherapy: examData?.physicalTherapy,
          smallOperatingCycles: examData?.smallOperatingCycles,
          specialistExaminations: examData?.specialistExaminations,
          reasonOfPurchase: examData?.reasonOfPurchase
        },
        source: isCrossell ? crossSellData?.newSoSource ?? 'crossell' : SourceEnum.DODATNO,
        ...(isCrossell && crossSellData ? { crossSell: {...crossSellData, newSoSource: SourceEnum.DODATNO} as CrossSellInput } : {}),
      };
      dispatch(setGlobalLoading(true));
      createSo(
        { input },
        {
          onSuccess: (response) => {
            dispatch(setSoId(response.data?.createSo));
            dispatch(setSoIdWeb(response.data?.createSo));
            dispatch(clearCrossSell());
            if (flow.flowType === 'web') {
              navigate(AppRoutesAdditional.OFFERS_PREVIEW.fullPath);
            } else {
              navigate(AppRoutesAdditional.THANK_YOU.fullPath);
            }
          },
          onError: () => {
            setGeneralError(i18n.t('pages.customerData.validation.error'));
          },
          onSettled: () => {
            dispatch(setGlobalLoading(false));
          },
        },
      );
    } else {
      dispatch(clearCustomer());
      dispatch(clearCustomerDataOld());
      dispatch(setExamDraft(null));
      navigate(routes.CUSTOMER_ONLY_DATE.fullPath);
    }
  };

  const customerInitialValues = {
    firstName: customerData?.firstName ?? '',
    lastName: customerData?.lastName ?? '',
    phoneNumber: customerData?.phoneNumber ?? '',
    email: customerData?.email ?? '',
    fullName: `${customerData?.firstName ?? ''} ${customerData?.lastName ?? ''}`.trim(),
  };

  const handleSubmit = () => {
    if (!isTermsAccepted) {
      setTermsError(i18n.t('pages.customerData.validation.termsRequired'));
      return;
    }
    setTermsError('');
    setGeneralError('');
    const valid = formRef.current?.validateForm();
    if (valid) {
      const formValues = formRef.current?.getValues();
      handleFormSuccess(formValues);
    }
  };

  return (
    <Flex vertical align="center" gap={32}>
      <Card>
        <Flex vertical gap={24}>
          <Text type="large" bold>{i18n.t('pages.customerData.title')}</Text>
          <CustomerFormAdditional ref={formRef} initialValues={customerInitialValues} onRequestCallClick={() => dispatch(openCallMeModal())} />
          <TermsCheckbox
            checked={isTermsAccepted}
            onChange={(checked: boolean) => {
              setIsTermsAccepted(checked);
              if (termsError) setTermsError('');
            }}
            error={termsError}
            title={i18n.t('pages.customerData.terms.title')}
            description={i18n.t('pages.customerData.terms.description')}
            errorTitle={i18n.t('pages.customerData.terms.errorTitle')}
            errorDescription={i18n.t('pages.customerData.terms.errorDescription')}
            requestCallButtonText={i18n.t('pages.customerData.terms.requestCallButton')}
            onRequestCallClick={() => dispatch(openCallMeModal())}
          />
          <Flex vertical gap={8}>
            <CaptchaChallenge {...captcha} />
            <Button
              onClick={handleSubmit}
              btnType="primary"
              size="large"
              text={i18n.t('pages.customerData.submitButton')}
              className="btnBlock"
              disabled={globalLoading || captcha.required}
            >
              <ArrowRightIcon color={colors.white} />
            </Button>
            {generalError && (
              <Text type="small" color={colors.error1} style={{ textAlign: 'center' }}>
                {generalError}
              </Text>
            )}
          </Flex>

          {/* GDPR text */}
          <Text type="small" color={colors.text1} style={{ textAlign: 'center', lineHeight: '150%' }}>
            {i18n.t('pages.customerData.gdprText').split('obrasca')[0]}
            <a href="https://kompare.hr/obrazac-za-prituzbe/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: colors.primary }}>obrasca</a>
            {i18n.t('pages.customerData.gdprText').split('obrasca')[1].split('szop@kompare.hr')[0]}
            <a href="mailto:szop@kompare.hr" style={{ textDecoration: 'underline', color: colors.primary }}>szop@kompare.hr</a>
            {i18n.t('pages.customerData.gdprText').split('szop@kompare.hr')[1].split('ovdje')[0]}
            <a href="https://kompare.hr/informacije-o-koristenju-osobnih-podataka/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: colors.primary }}>ovdje</a>
            {i18n.t('pages.customerData.gdprText').split('ovdje')[1]}
          </Text>
        </Flex>
      </Card>
      <Flex justify="center">
        <BackLink text={i18n.t('pages.customerData.backLink')} path={`${routes.HEALTH_EXAMS.fullPath}/${customerData?.birthDate}`} />
      </Flex>
    </Flex>
  );
};

export default CustomerDataPage;
